import { useEffect, useState } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";
import { PaginationBar } from "@/components/shared/PaginationBar";
import { usePaymentDeskRefData } from "./shared/usePaymentDeskRefData";
import BillFormDialog from "./BillFormDialog";

const CREATE_ROLES = ["Director", "Manager", "Accounts", "Back Office"];
const STATUS_OPTIONS = ["Outstanding", "Partially Paid", "Paid"];

function money(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

const STATUS_BADGE = { Outstanding: "bg-amber-500", "Partially Paid": "bg-blue-500", Paid: "bg-emerald-600" };

// Vendor Bills + Payables — Back Office/Manager/Director record a bill the
// moment material/service arrives on credit; this same filterable list IS
// the Payables view for all four roles (status is always computed, never
// set manually).
export default function VendorBillsTab() {
  const { user } = useAuth();
  const canCreate = CREATE_ROLES.includes(user?.role);
  const refData = usePaymentDeskRefData();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendorFilter, setVendorFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [accFilter, setAccFilter] = useState("all");
  const [formTarget, setFormTarget] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const load = (pageNum = 1) => {
    setLoading(true);
    const params = { page: pageNum, page_size: 50 };
    if (vendorFilter !== "all") params.vendor_code = vendorFilter;
    if (siteFilter !== "all") params.site_id = siteFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    if (accFilter !== "all") params.accommodation = accFilter;
    client
      .get("/payment-desk/bills", { params })
      .then((res) => {
        setBills(res.data.items);
        setPage(res.data.page);
        setTotalPages(res.data.total_pages);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(1); }, [vendorFilter, siteFilter, statusFilter, accFilter]);

  return (
    <div className="space-y-4" data-testid="vendor-bills-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <SearchableSelect testId="bill-vendor-filter" className="w-48" value={vendorFilter} onValueChange={setVendorFilter} placeholder="All Vendors"
  options={[{ value: "all", label: "All Vendors", pinned: true }, ...refData.vendors.map((v) => ({ value: v.vendor_code, label: v.vendor_name, searchText: v.vendor_code }))]} />
          <SearchableSelect testId="bill-site-filter" className="w-48" value={siteFilter} onValueChange={setSiteFilter} placeholder="All Sites"
  options={[{ value: "all", label: "All Sites", pinned: true }, ...refData.sites.map((s) => ({ value: s.site_id, label: s.display_label || s.label }))]} />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="bill-status-filter" className="w-40">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={accFilter} onValueChange={setAccFilter}>
            <SelectTrigger data-testid="bill-accommodation-filter" className="w-48">
              <SelectValue placeholder="All Bills" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Bills</SelectItem>
              <SelectItem value="only">Accommodation Only</SelectItem>
              <SelectItem value="hide">Hide Accommodation</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {canCreate && (
          <Button data-testid="bill-add-button" onClick={() => setFormTarget("new")} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-1.5" /> Add Vendor Bill
          </Button>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="bill-table">
          <TableHeader>
            <TableRow>
              <TableHead>Bill ID</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Attributed To</TableHead>
              <TableHead>Room</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Bill Amount</TableHead>
              <TableHead>Outstanding</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Bill Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
            ) : bills.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">No vendor bills found.</TableCell></TableRow>
            ) : (
              bills.map((b) => (
                <TableRow key={b.bill_id} data-testid={`bill-row-${b.bill_id}`} className="cursor-pointer" onClick={() => setFormTarget(b)}>
                  <TableCell className="font-mono text-xs font-semibold">{b.bill_id}</TableCell>
                  <TableCell>{refData.vendors.find((v) => v.vendor_code === b.vendor_code)?.vendor_name || b.vendor_code}</TableCell>
                  <TableCell className="text-xs">{b.attribution_label}</TableCell>
                  <TableCell className="font-mono text-xs" data-testid={`bill-room-${b.bill_id}`}>{b.accommodation_id || "—"}</TableCell>
                  <TableCell className="text-xs">{b.category_name}</TableCell>
                  <TableCell>{money(b.amount)}</TableCell>
                  <TableCell className="font-medium">{money(b.outstanding_balance)}</TableCell>
                  <TableCell><Badge className={STATUS_BADGE[b.status]}>{b.status}</Badge></TableCell>
                  <TableCell className="text-xs text-slate-500">{new Date(b.bill_date).toLocaleDateString("en-IN")}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} onPageChange={load} testId="bill-pagination" />

      {formTarget && (
        <BillFormDialog
          bill={formTarget === "new" ? null : formTarget}
          refData={refData}
          readOnly={!canCreate}
          onClose={() => setFormTarget(null)}
          onSaved={() => {
            setFormTarget(null);
            load(page);
          }}
        />
      )}
    </div>
  );
}
